import React, { useState, useEffect, useRef } from 'react';
import { Loader2, AlertCircle, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';

const SharedBoardView = ({ shareToken, onNavigate }) => {
  const { t } = useTranslation();
  const [board, setBoard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const loadSharedBoard = async () => {
      try {
        const data = await api.getSharedBoard(shareToken);
        setBoard({
          ...data.board,
          parsedData: JSON.parse(data.board.data || '{}')
        });
      } catch (err) {
        console.error('Error loading shared board:', err);
        setError(t('share.boardLoadFailed'));
      }
      setIsLoading(false);
    };

    loadSharedBoard();
  }, [shareToken, t]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (error || !board) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-10 h-10 text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('share.boardNotFound')}</h2>
          <p className="text-gray-600 mb-6">{t('share.boardNotFoundDescription')}</p>
          <button
            onClick={() => onNavigate('/')}
            className="w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition font-medium"
          >
            {t('share.backToHome')}
          </button>
        </div>
      </div>
    );
  }

  const canvasImages = board.parsedData.canvasImages || [];
  const canvasElements = board.parsedData.canvasElements || [];
  const background = board.parsedData.background || { type: 'gradient', value: 'from-gray-50 to-gray-100' };
  const canvasFrame = board.parsedData.canvasFrame;
  const canvasSize = board.parsedData.canvasSize || { width: 210, height: 297 };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="logo.png"
              alt={t('common.appName')}
              className="h-8"
            />
            <div className="hidden sm:block">
              <p className="text-sm text-gray-500">{t('share.sharedBy')}</p>
              <h1 className="font-semibold text-gray-900">{board.title || t('editor.visionBoard')}</h1>
            </div>
          </div>
          <a
            href="/#/"
            className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition text-sm font-medium"
          >
            {t('landing.hero.cta')}
          </a>
        </div>
      </div>

      {/* Board Title (Mobile) */}
      <div className="sm:hidden px-4 py-3 bg-white border-b border-gray-100">
        <p className="text-sm text-gray-500">{t('share.sharedBy')}</p>
        <h1 className="font-semibold text-gray-900">{board.title || t('editor.visionBoard')}</h1>
      </div>

      {/* Canvas */}
      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6">
          <div
            ref={canvasRef}
            className={`relative mx-auto overflow-hidden ${
              background.type === 'gradient' ? `bg-gradient-to-br ${background.value}` :
              background.type === 'solid' ? background.value :
              'bg-gray-100'
            }`}
            style={{
              width: '100%',
              maxWidth: '800px',
              aspectRatio: `${canvasSize.width} / ${canvasSize.height}`,
              ...(background.type === 'pattern' && background.value === 'dots' ? {
                backgroundImage: 'radial-gradient(circle, #ddd 1px, transparent 1px)',
                backgroundSize: '20px 20px'
              } : {}),
              ...(background.type === 'pattern' && background.value === 'grid' ? {
                backgroundImage: 'linear-gradient(#ddd 1px, transparent 1px), linear-gradient(90deg, #ddd 1px, transparent 1px)',
                backgroundSize: '20px 20px'
              } : {})
            }}
          >
            {/* Canvas Images */}
            {canvasImages.map((img) => (
              <img
                key={img.id}
                src={img.imageUrl}
                alt=""
                className="absolute object-cover"
                style={{
                  left: `${(img.x / 800) * 100}%`,
                  top: `${(img.y / 800) * 100}%`,
                  width: `${(img.width / 800) * 100}%`,
                  height: `${(img.height / 800) * 100}%`,
                  transform: `rotate(${img.rotation || 0}deg)`,
                  zIndex: img.zIndex || 0,
                }}
              />
            ))}

            {/* Canvas Elements (emojis, text) */}
            {canvasElements.map((el) => (
              <div
                key={el.id}
                className="absolute"
                style={{
                  left: `${(el.x / 800) * 100}%`,
                  top: `${(el.y / 800) * 100}%`,
                  transform: `rotate(${el.rotation || 0}deg)`,
                  zIndex: el.zIndex || 0,
                }}
              >
                {el.type === 'emoji' ? (
                  <span style={{ fontSize: `${((el.size || 48) / 800) * 100}vw`, maxWidth: '48px' }}>{el.content}</span>
                ) : (
                  <p style={{
                    color: el.color || '#000000',
                    fontSize: `${((el.fontSize || 24) / 800) * 100}vw`,
                    fontWeight: el.fontWeight || 'normal',
                    fontStyle: el.fontStyle || 'normal',
                    textAlign: el.textAlign || 'left',
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                  }}>
                    {el.content}
                  </p>
                )}
              </div>
            ))}

            {/* Frame */}
            {canvasFrame && (
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  border: canvasFrame.border,
                  boxShadow: canvasFrame.shadow,
                  borderBottom: canvasFrame.bottomBorder ? `${canvasFrame.bottomBorder} solid #fff` : canvasFrame.border,
                }}
              />
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-6 text-center">
          <p className="text-gray-600 mb-4">{t('share.ctaPrompt')}</p>
          <a
            href="/#/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition font-medium shadow-lg"
          >
            <Sparkles className="w-5 h-5" />
            {t('landing.hero.cta')}
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-6 px-4 text-center text-gray-500 text-sm">
        <div className="flex flex-wrap items-center justify-center gap-4 text-sm mb-2">
          <a href="/#/support" className="hover:text-gray-800 transition">{t('common.support')}</a>
          <a href="/#/privacy" className="hover:text-gray-800 transition">{t('common.privacy')}</a>
          <a href="/#/terms" className="hover:text-gray-800 transition">{t('common.terms')}</a>
        </div>
        <p>{t('landing.footer.copyright', { year: new Date().getFullYear() })}</p>
      </footer>
    </div>
  );
};

export default SharedBoardView;
